import { useState, useEffect } from "react";
import useAppStore from "src/stores/appStore";
import { CALL_ME_FIELDS } from "@phrasing";
import { assistantIdSelector, isOutboundAgentSelector, systemModeSelector, selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import { cleanPhoneNumber, validatePhoneNumber } from "@utils";
import { useFetchCallMeSites, useFetchCallMeBusinessHours } from "src/queries/hooks/assistants";
import { migrateOutboundJsonData, OUTBOUND_JSON_KEY, PHONE_NUMBER_KEY, isOutboundJsonValid, getInitialPhoneNumber, getInitialTaskData } from "./CallMeDialog.helpers";

const useCallMeDialog = () => {
  const assistant = useAppStore(selectedAssistantSelector);
  const systemMode = useAppStore(systemModeSelector);
  const previousSystemMode = useAppStore(systemModeSelector);
  const isOutboundAgent = useAppStore(isOutboundAgentSelector);
  const realSystemMode = systemMode === "preview" ? previousSystemMode : systemMode;
  const assistantId = useAppStore(assistantIdSelector);
  const [errors, setErrors] = useState({});

  const { data: sitesData, isLoading: isSitesLoading, isError: isSitesError } = useFetchCallMeSites(assistantId, realSystemMode, isOutboundAgent);
  const { data: businessHoursData, isLoading: isBusinessHoursLoading, isError: isBusinessHoursError } = useFetchCallMeBusinessHours(assistantId, realSystemMode, isOutboundAgent);
  const loading = isSitesLoading || isBusinessHoursLoading;

  useEffect(() => {
    if (isSitesError) console.error("Failed to fetch Call Me sites");
    if (isBusinessHoursError) console.error("Failed to fetch Call Me business hours");
  }, [isSitesError, isBusinessHoursError]);

  const sites = isSitesError ? [{ key: "", display_key: "Error loading sites. Please try again later.", disabled: true }] : sitesData;
  const businessHoursModes = isBusinessHoursError ? [{ key: "", display_key: "Error loading operating hours. Please try again later.", disabled: true }] : businessHoursData;

  const requiresOutboundJson = isOutboundAgent;

  useEffect(() => {
    if (assistantId && requiresOutboundJson) {
      migrateOutboundJsonData(assistantId);
    }
  }, [assistantId, requiresOutboundJson]);

  const [fields, setFields] = useState({ phoneNumber: getInitialPhoneNumber(), callerId: "", site: "", businessHoursMode: "", outboundJson: getInitialTaskData(requiresOutboundJson, assistantId) });

  useEffect(() => {
    if (businessHoursModes?.length > 0 && fields.businessHoursMode === null) {
      const defaultMode = businessHoursModes.find(m => m.display_key === "On hours") || businessHoursModes[1];
      setFields(prev => ({ ...prev, businessHoursMode: defaultMode?.key ?? "" }));
    }
  }, [businessHoursModes, fields.businessHoursMode]);

  const disableCall = Object.values(errors)?.length > 0 || fields.phoneNumber === "" || (requiresOutboundJson && !isOutboundJsonValid(fields.outboundJson));

  const fieldsForServer = {
    ...fields,
    phoneNumber: fields.phoneNumber.startsWith("+") ? fields.phoneNumber : `+1${fields.phoneNumber}`,
    callerId: fields.callerId.startsWith("+") ? fields.callerId : fields.callerId.length > 0 ? `+1${fields.callerId}` : ""
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };
    const isPhoneNumberField = name === CALL_ME_FIELDS.PHONE_NUMBER.ID || name === CALL_ME_FIELDS.CALLER_ID.ID;

    // Validate phone number fields
    if (isPhoneNumberField) {
      const phoneValidation = validatePhoneNumber(value) || validatePhoneNumber(value, "IL");

      if (phoneValidation) {
        delete newErrors[name];
      } else {
        const snakeCaseName = name.replace(/([A-Z])/g, "_$1").toUpperCase();
        newErrors = { ...newErrors, [name]: CALL_ME_FIELDS[snakeCaseName].ERROR };
      }
    }

    // Validate outbound json field
    if (name === CALL_ME_FIELDS.OUTBOUND_JSON.ID) {
      if (!isOutboundJsonValid(value)) {
        newErrors[name] = CALL_ME_FIELDS.OUTBOUND_JSON.ERROR;
      } else {
        delete newErrors[name];
      }
    }

    // Save user response in local storage
    if (name === CALL_ME_FIELDS.OUTBOUND_JSON.ID && !newErrors[CALL_ME_FIELDS.OUTBOUND_JSON.ID]) {
      localStorage.setItem(`${assistantId}:${OUTBOUND_JSON_KEY}`, value);
    }
    if (name === CALL_ME_FIELDS.PHONE_NUMBER.ID && (!newErrors[CALL_ME_FIELDS.PHONE_NUMBER.ID] || !value)) {
      localStorage.setItem(PHONE_NUMBER_KEY, value);
    }

    const formattedValue = isPhoneNumberField ? cleanPhoneNumber(value) : value;

    setFields({ ...fields, [name]: value && formattedValue });
    setErrors(newErrors);
  };

  return { sites, businessHoursModes, handleChange, disableCall, errors, fields, loading, fieldsForServer, systemMode };
};

export default useCallMeDialog;
